import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 6: Продвинутые техники
 */
const module6: CourseModule = {
  id: 'module-6',
  title: 'Методологии ветвления',
  slug: 'branching-methodologies',
  order: 6,
  lessons: [
    {
      id: '6-1',
      moduleId: 'module-6',
      title: 'Введение в методологии ветвления',
      slug: 'branching-methodologies-intro',
      order: 1,
      theory: `# Введение в методологии ветвления

Методология ветвления — это набор правил и соглашений о том, как команда работает с ветками в Git.

## Зачем нужна методология?

Без чёткой методологии:
- Хаос в названиях веток
- Непонятно, когда и куда сливать код
- Конфликты и дублирование работы
- Сложно отследить историю изменений

С методологией:
- Понятная структура веток
- Предсказуемый процесс разработки
- Легче находить и исправлять баги
- Упрощается Code Review

## Основные методологии

### Git Flow
Классическая модель с длинными ветками и строгими правилами. Идеально для проектов с регулярными релизами.

### GitHub Flow
Упрощённая модель для continuous deployment. Одна основная ветка и короткоживущие feature-ветки.

### GitLab Flow
Гибридный подход с environment branches. Подходит для проектов с несколькими окружениями.

### Trunk-Based Development
Минимум веток, частые слияния в main. Для опытных команд с хорошим тестированием.

## Сравнение методологий

| Методология | Сложность | Релизы | Команда | CI/CD |
|-------------|-----------|--------|---------|-------|
| Git Flow | Высокая | Релизные циклы | Любая | Нет |
| GitHub Flow | Низкая | Continuous | Малая-средняя | Да |
| GitLab Flow | Средняя | Гибкие | Средняя-большая | Да |
| Trunk-Based | Низкая | Continuous | Опытная | Обязательно |

## Выбор методологии

При выборе учитывайте:
1. **Размер команды** — больше людей = больше структуры
2. **Частота релизов** — часто = упрощённые модели
3. **Опыт команды** — новички = простые модели
4. **Требования к стабильности** — критичные системы = строгие правила
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр текущих веток',
          description: 'Посмотрите, какие ветки существуют в репозитории.',
          expectedCommand: 'git branch -a',
          hint: {
            level1: 'Посмотрите все ветки в репозитории.',
            level2: 'Используйте git branch -a.',
            level3: 'Введите: git branch -a',
          },
        },
      ],
      keyInsight: {
        main: 'Методология ветвления — это договорённость команды о том, как работать с Git. Выберите подходящую и следуйте ей.',
        example:
          'Команда из 3 разработчиков может использовать GitHub Flow: создали ветку feature/login, написали код, открыли PR, проверили, влили в main. Команда из 20 человек уже нуждается в Git Flow или GitLab Flow.',
        warning:
          'Не меняйте методологию в середине проекта без веской причины. Это создаст путаницу и сломает процессы.',
        tip: 'Начните с простой методологии (GitHub Flow) и усложняйте только когда появится реальная потребность.',
        additional: [
          'Методология должна быть задокументирована и доступна всем участникам.',
          'Автоматизируйте проверки (CI) для соблюдения правил методологии.',
          'Периодически пересматривайте методологию на ретроспективах.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '6-1-q1',
          question: 'Какая методология ветвления лучше всего подходит для continuous deployment?',
          options: [
            {
              id: 'a',
              text: 'Git Flow',
              isCorrect: false,
              explanation:
                'Неправильно. Git Flow сложный и не оптимален для continuous deployment.',
            },
            {
              id: 'b',
              text: 'GitHub Flow',
              isCorrect: true,
              explanation: 'Правильно! GitHub Flow прост и идеален для continuous deployment.',
            },
            {
              id: 'c',
              text: 'GitLab Flow',
              isCorrect: false,
              explanation: 'Неправильно. GitLab Flow — гибридный подход.',
            },
            {
              id: 'd',
              text: 'Trunk-Based Development',
              isCorrect: false,
              explanation: 'Неправильно. Хотя подходит, GitHub Flow более распространён для CD.',
            },
          ],
          explanation:
            'GitHub Flow — простая модель с одной основной веткой и короткоживущими feature-ветками. Идеально подходит для continuous deployment.',
          difficulty: 'medium',
        },
        {
          id: '6-1-q2',
          question: 'Какая основная проблема возникает без чёткой методологии ветвления?',
          options: [
            {
              id: 'a',
              text: 'Увеличение скорости разработки',
              isCorrect: false,
              explanation: 'Неправильно. Без методологии скорость обычно снижается.',
            },
            {
              id: 'b',
              text: 'Хаос в ветках и непонятно, когда и куда сливать код',
              isCorrect: true,
              explanation: 'Правильно! Без методологии команда теряется в ветках.',
            },
            {
              id: 'c',
              text: 'Улучшение качества кода',
              isCorrect: false,
              explanation: 'Неправильно. Без методологии качество обычно страдает.',
            },
            {
              id: 'd',
              text: 'Автоматическое тестирование',
              isCorrect: false,
              explanation: 'Неправильно. Это не связано напрямую с методологией.',
            },
          ],
          explanation:
            'Без чёткой методологии ветвления команда сталкивается с хаосом: непонятные названия веток, конфликты, дублирование работы.',
          difficulty: 'easy',
        },
        {
          id: '6-1-q3',
          question:
            'Какая методология использует длинные ветки и строгие правила для релизных циклов?',
          options: [
            {
              id: 'a',
              text: 'GitHub Flow',
              isCorrect: false,
              explanation: 'Неправильно. GitHub Flow — упрощённая модель.',
            },
            {
              id: 'b',
              text: 'Trunk-Based Development',
              isCorrect: false,
              explanation: 'Неправильно. Trunk-Based минимизирует ветки.',
            },
            {
              id: 'c',
              text: 'Git Flow',
              isCorrect: true,
              explanation:
                'Правильно! Git Flow имеет develop, release, master ветки и строгие правила.',
            },
            {
              id: 'd',
              text: 'GitLab Flow',
              isCorrect: false,
              explanation: 'Неправильно. GitLab Flow — гибридный подход.',
            },
          ],
          explanation:
            'Git Flow — классическая модель с develop, release, master ветками. Подходит для проектов с регулярными релизами.',
          difficulty: 'medium',
        },
        {
          id: '6-1-q4',
          question: 'Для каких команд лучше всего подходит Trunk-Based Development?',
          options: [
            {
              id: 'a',
              text: 'Начинающие команды',
              isCorrect: false,
              explanation: 'Неправильно. Начинающим сложно работать без веток.',
            },
            {
              id: 'b',
              text: 'Опытные команды с хорошим тестированием',
              isCorrect: true,
              explanation: 'Правильно! Требует дисциплины и автоматических тестов.',
            },
            {
              id: 'c',
              text: 'Команды с длинными релизными циклами',
              isCorrect: false,
              explanation: 'Неправильно. Trunk-Based для частых релизов.',
            },
            {
              id: 'd',
              text: 'Команды без CI/CD',
              isCorrect: false,
              explanation: 'Неправильно. CI/CD обязателен для Trunk-Based.',
            },
          ],
          explanation:
            'Trunk-Based Development требует опытной команды, хорошей культуры code review и надёжных автоматических тестов.',
          difficulty: 'hard',
        },
      ],
      resources: [
        {
          title: 'Git Branching Strategies',
          url: 'https://git-scm.com/book/en/v2/Distributed-Git-Distributed-Workflows',
          type: 'book',
          description: 'Глава Pro Git о распределённых рабочих процессах',
        },
        {
          title: 'Comparing Workflows',
          url: 'https://www.atlassian.com/git/tutorials/comparing-workflows',
          type: 'article',
          description: 'Сравнение рабочих процессов от Atlassian',
        },
        {
          title: 'Git Branching Best Practices',
          url: 'https://deepsource.io/blog/git-branching-strategies/',
          type: 'article',
          description: 'Лучшие практики ветвления от DeepSource',
        },
      ],
    },
    {
      id: '6-2',
      moduleId: 'module-6',
      title: 'Git Flow',
      slug: 'git-flow',
      order: 2,
      theory: `# Git Flow

Git Flow — классическая модель ветвления, предложенная Vincent Driessen в 2010 году.

## Структура веток

### Главные ветки (долгоживущие)

- **main/master** — стабильный код, готовый к продакшену
- **develop** — ветка разработки, содержит следующий релиз

### Вспомогательные ветки (короткоживущие)

- **feature/\*** — новые функции (от develop)
- **release/\*** — подготовка к релизу (от develop)
- **hotfix/\*** — срочные исправления (от main)
- **bugfix/\*** — исправление багов (от develop)

## Диаграмма Git Flow

\`\`\`
main        ──●────●────●─────────●──
            /      \\      \\       /
release    /        ●──────●─────●
          /        /              \\
develop   ●────●────●────●────●────●
          \\            /
feature    ●────●────●
\`\`\`

## Рабочий процесс

### 1. Начало работы над функцией

\`\`\`bash
# Переключиться на develop
git checkout develop
git pull origin develop

# Создать ветку функции
git checkout -b feature/user-authentication
\`\`\`

### 2. Разработка функции

\`\`\`bash
# Работа над функцией...
git add .
git commit -m "Добавить форму входа"
git push -u origin feature/user-authentication
\`\`\`

### 3. Завершение функции

\`\`\`bash
# Слить в develop (через PR или напрямую)
git checkout develop
git merge --no-ff feature/user-authentication
git push origin develop

# Удалить ветку функции
git branch -d feature/user-authentication
git push origin --delete feature/user-authentication
\`\`\`

### 4. Создание релиза

\`\`\`bash
# Создать ветку релиза
git checkout develop
git checkout -b release/1.0.0

# Только багфиксы и документация
# Обновить версию, CHANGELOG

# Слить в main и develop
git checkout main
git merge --no-ff release/1.0.0
git tag -a v1.0.0 -m "Версия 1.0.0"

git checkout develop
git merge --no-ff release/1.0.0

# Удалить ветку релиза
git branch -d release/1.0.0
\`\`\`

### 5. Hotfix

\`\`\`bash
# Создать hotfix от main
git checkout main
git checkout -b hotfix/critical-bug

# Исправить баг
git commit -m "Исправить критический баг"

# Слить в main и develop
git checkout main
git merge --no-ff hotfix/critical-bug
git tag -a v1.0.1 -m "Hotfix 1.0.1"

git checkout develop
git merge --no-ff hotfix/critical-bug

git branch -d hotfix/critical-bug
\`\`\`

## Инструменты Git Flow

\`\`\`bash
# Установка git-flow (macOS)
brew install git-flow

# Инициализация в проекте
git flow init

# Начать feature
git flow feature start user-auth

# Завершить feature
git flow feature finish user-auth

# Начать release
git flow release start 1.0.0

# Завершить release
git flow release finish 1.0.0

# Начать hotfix
git flow hotfix start critical-bug

# Завершить hotfix
git flow hotfix finish critical-bug
\`\`\`

## Когда использовать Git Flow?

**Подходит:**
- Проекты с регулярными релизами (раз в неделю/месяц)
- Команды любого размера
- Проекты, требующие стабильности продакшена

**Не подходит:**
- Continuous deployment
- Быстрые итерации (несколько релизов в день)
- Малые проекты с простой структурой
`,
      steps: [
        {
          id: 1,
          title: 'Создание ветки функции',
          description: 'Создайте ветку функции от develop.',
          expectedCommand: 'git checkout -b feature/new-feature develop',
          validationRegex: 'git (checkout|switch)\\s+-b\\s+feature/.*',
          hint: {
            level1: 'Создайте feature-ветку для новой функции.',
            level2: 'Используйте git checkout -b feature/название develop.',
            level3: 'Введите: git checkout -b feature/new-feature develop',
          },
        },
      ],
      keyInsight: {
        main: 'Git Flow — структурированная модель для проектов с регулярными релизами. Используйте develop для разработки, main для продакшена.',
        example:
          'Типичный сценарий: разработчик создаёт feature/login, работает неделю, сливает в develop. Когда набирается достаточно функций, создаётся release/1.2.0, тестируется неделю, сливается в main с тегом v1.2.0.',
        warning:
          'Не коммитьте напрямую в main! В Git Flow main содержит только проверенный код релизов.',
        tip: 'Используйте флаг --no-ff при слиянии для сохранения истории ветки. Это упрощает откат и понимание изменений.',
        additional: [
          'Ветка develop должна быть всегда готова к созданию релиза.',
          'Hotfix ветки создаются от main, не от develop.',
          'Теги на main — это версии продакшена.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '6-2-q1',
          question: 'Какая ветка в Git Flow является основной для разработки?',
          options: [
            {
              id: 'a',
              text: 'main/master',
              isCorrect: false,
              explanation: 'Неправильно. main содержит только стабильный production код.',
            },
            {
              id: 'b',
              text: 'develop',
              isCorrect: true,
              explanation: 'Правильно! develop — ветка интеграции для feature-веток.',
            },
            {
              id: 'c',
              text: 'release',
              isCorrect: false,
              explanation: 'Неправильно. release создаётся для подготовки релиза.',
            },
            {
              id: 'd',
              text: 'feature',
              isCorrect: false,
              explanation: 'Неправильно. feature — это временные ветки для функций.',
            },
          ],
          explanation:
            'В Git Flow develop — это основная ветка разработки. Feature-ветки создаются от develop и сливаются обратно в develop.',
          difficulty: 'easy',
        },
        {
          id: '6-2-q2',
          question: 'От какой ветки создаются release-ветки в Git Flow?',
          options: [
            {
              id: 'a',
              text: 'От main',
              isCorrect: false,
              explanation: 'Неправильно. Release-ветки создаются от develop.',
            },
            {
              id: 'b',
              text: 'От develop',
              isCorrect: true,
              explanation: 'Правильно! Release-ветки создаются от develop для подготовки релиза.',
            },
            {
              id: 'c',
              text: 'От feature',
              isCorrect: false,
              explanation: 'Неправильно. Feature-ветки не используются для создания release.',
            },
            {
              id: 'd',
              text: 'От hotfix',
              isCorrect: false,
              explanation: 'Неправильно. Hotfix — это исправления в production.',
            },
          ],
          explanation:
            'Release-ветки создаются от develop, когда разработка готова к релизу. После тестирования release сливается в main и develop.',
          difficulty: 'medium',
        },
        {
          id: '6-2-q3',
          question: 'Куда сливается hotfix-ветка после исправления критического бага?',
          options: [
            {
              id: 'a',
              text: 'Только в main',
              isCorrect: false,
              explanation: 'Неправильно. Hotfix сливается и в main, и в develop.',
            },
            {
              id: 'b',
              text: 'Только в develop',
              isCorrect: false,
              explanation: 'Неправильно. Hotfix сливается и в main, и в develop.',
            },
            {
              id: 'c',
              text: 'В main и develop',
              isCorrect: true,
              explanation:
                'Правильно! Hotfix сливается в обе ветки, чтобы исправление попало и в production, и в разработку.',
            },
            {
              id: 'd',
              text: 'В release',
              isCorrect: false,
              explanation: 'Неправильно. Hotfix не связан с release-ветками.',
            },
          ],
          explanation:
            'Hotfix-ветки создаются от main для срочных исправлений. После исправления они сливаются в main (с тегом) и в develop.',
          difficulty: 'medium',
        },
        {
          id: '6-2-q4',
          question: 'Какой недостаток Git Flow для continuous deployment?',
          options: [
            {
              id: 'a',
              text: 'Слишком простая структура',
              isCorrect: false,
              explanation: 'Неправильно. Git Flow наоборот сложный.',
            },
            {
              id: 'b',
              text: 'Слишком много веток и долгий путь до production',
              isCorrect: true,
              explanation: 'Правильно! Git Flow не оптимален для частых деплоев.',
            },
            {
              id: 'c',
              text: 'Нет поддержки feature-веток',
              isCorrect: false,
              explanation: 'Неправильно. Git Flow активно использует feature-ветки.',
            },
            {
              id: 'd',
              text: 'Не поддерживает release-ветки',
              isCorrect: false,
              explanation: 'Неправильно. Release-ветки — ключевая часть Git Flow.',
            },
          ],
          explanation:
            'Git Flow сложный для continuous deployment: develop → release → main. Для CD лучше подходит GitHub Flow или Trunk-Based Development.',
          difficulty: 'hard',
        },
      ],
      resources: [
        {
          title: 'Git Flow Original Article',
          url: 'https://nvie.com/posts/a-successful-git-branching-model/',
          type: 'article',
          description: 'Оригинальная статья Vincent Driessen о Git Flow',
        },
        {
          title: 'git-flow Extension',
          url: 'https://github.com/nvie/gitflow',
          type: 'tool',
          description: 'Официальный git-flow extension',
        },
        {
          title: 'Git Flow Cheatsheet',
          url: 'https://danielkummer.github.io/git-flow-cheatsheet/',
          type: 'article',
          description: 'Шпаргалка по командам git-flow',
        },
      ],
    },
    {
      id: '6-3',
      moduleId: 'module-6',
      title: 'GitHub Flow',
      slug: 'github-flow',
      order: 3,
      theory: `# GitHub Flow

GitHub Flow — упрощённая модель ветвления, разработанная GitHub для continuous deployment.

## Принципы

1. **main всегда готов к деплою** — код в main можно развернуть в любой момент
2. **Ветки для всего** — каждое изменение в отдельной ветке
3. **PR обязателен** — код проверяется перед слиянием
4. **Деплой после слияния** — изменения попадают на прод сразу после PR

## Структура веток

\`\`\`
main    ●────●────●────●────●────●
        /     /     /     /
feature ●────●     ●────●
\`\`\`

Только одна долгоживая ветка — **main**. Все остальные ветки — feature-ветки.

## Рабочий процесс

### 1. Создание ветки

\`\`\`bash
# Обновить main
git checkout main
git pull origin main

# Создать ветку
git checkout -b feature/add-search
\`\`\`

Именование веток:
- \`feature/\*\` — новые функции
- \`bugfix/\*\` — исправление багов
- \`hotfix/\*\` — срочные исправления
- \`docs/\*\` — документация

### 2. Разработка

\`\`\`bash
# Делайте частые коммиты
git add .
git commit -m "Добавить поле поиска"
git push -u origin feature/add-search

# Продолжайте работу...
git commit -m "Добавить фильтры"
git push
\`\`\`

### 3. Создание Pull Request

\`\`\`bash
# Push ветки (если ещё не сделали)
git push -u origin feature/add-search
\`\`\`

Затем создайте PR через веб-интерфейс GitHub.

### 4. Code Review

- Обсуждение кода в PR
- Автоматические проверки (CI)
- Исправление замечаний

\`\`\`bash
# После замечаний ревьюера
git add .
git commit -m "Исправить по ревью"
git push
\`\`\`

### 5. Слияние и деплой

\`\`\`bash
# После одобрения PR сливается в main
# Автоматический деплой на продакшен

# Обновить локальный main
git checkout main
git pull origin main

# Удалить ветку
git branch -d feature/add-search
\`\`\`

## Защита ветки main

Настройте в GitHub:
- Require pull request reviews
- Require status checks to pass
- Require signed commits
- Include administrators

## Сравнение с Git Flow

| Аспект | GitHub Flow | Git Flow |
|--------|-------------|----------|
| Веток | 1 главная | 2 главные + вспомогательные |
| Релизы | Continuous | По расписанию |
| Сложность | Низкая | Высокая |
| Подходит | Web-приложения | Коробочный софт |

## Когда использовать GitHub Flow?

**Подходит:**
- Continuous deployment
- Web-приложения и SaaS
- Малые и средние команды
- Быстрые итерации

**Не подходит:**
- Проекты с длительным тестированием
- Множество поддерживаемых версий
- Требование стабильности продакшена
`,
      steps: [
        {
          id: 1,
          title: 'Создание feature-ветки',
          description: 'Создайте ветку для новой функции от main.',
          expectedCommand: 'git checkout -b feature/github-flow-demo main',
          validationRegex: 'git (checkout|switch)\\s+-b\\s+feature/.*',
          hint: {
            level1: 'Создайте feature-ветку от main.',
            level2: 'Используйте git checkout -b feature/название main.',
            level3: 'Введите: git checkout -b feature/github-flow-demo main',
          },
        },
        {
          id: 2,
          title: 'Проверка текущей ветки',
          description: 'Убедитесь, что вы на feature-ветке.',
          expectedCommand: 'git branch',
          hint: {
            level1: 'Проверьте текущую ветку.',
            level2: 'Используйте git branch.',
            level3: 'Введите: git branch',
          },
        },
      ],
      keyInsight: {
        main: 'GitHub Flow — простая модель для continuous deployment. Одна главная ветка, все изменения через Pull Request.',
        example:
          'Разработчик создаёт feature/dark-mode, делает 5 коммитов за день, открывает PR, получает одобрение, сливает в main. Через 10 минут тёмная тема уже на продакшене.',
        warning:
          'main должен быть всегда готов к деплою! Настройте CI-проверки и обязательные ревью.',
        tip: 'Делайте маленькие PR — до 400 строк кода. Их легче проверять и быстрее сливать.',
        additional: [
          'Используйте Draft PR для работы в процессе.',
          'Настройте автоматический деплой при слиянии в main.',
          'Именуйте ветки понятно: feature/, bugfix/, hotfix/.',
        ],
      },
      estimatedTime: 20,
      quizQuestions: [
        {
          id: '6-3-q1',
          question: 'Сколько долгоживущих веток в GitHub Flow?',
          options: [
            {
              id: 'a',
              text: 'Две: main и develop',
              isCorrect: false,
              explanation: 'Неправильно. В GitHub Flow только одна долгоживущая ветка.',
            },
            {
              id: 'b',
              text: 'Одна: main',
              isCorrect: true,
              explanation: 'Правильно! GitHub Flow имеет только одну долгоживущую ветку — main.',
            },
            {
              id: 'c',
              text: 'Три: main, develop, release',
              isCorrect: false,
              explanation: 'Неправильно. Это Git Flow, не GitHub Flow.',
            },
            {
              id: 'd',
              text: 'Нет долгоживущих веток',
              isCorrect: false,
              explanation: 'Неправильно. Main — долгоживущая ветка.',
            },
          ],
          explanation:
            'GitHub Flow — простая модель с одной долгоживущей веткой main. Все feature-ветки короткоживущие и сливаются обратно в main.',
          difficulty: 'easy',
        },
        {
          id: '6-3-q2',
          question: 'Что происходит с веткой после слияния в main в GitHub Flow?',
          options: [
            {
              id: 'a',
              text: 'Ветка остаётся навсегда',
              isCorrect: false,
              explanation: 'Неправильно. Ветки удаляются после слияния.',
            },
            {
              id: 'b',
              text: 'Ветка удаляется',
              isCorrect: true,
              explanation: 'Правильно! После слияния ветка удаляется для поддержания чистоты.',
            },
            {
              id: 'c',
              text: 'Ветка переименовывается',
              isCorrect: false,
              explanation: 'Неправильно. Ветки не переименовываются.',
            },
            {
              id: 'd',
              text: 'Ветка архивируется',
              isCorrect: false,
              explanation: 'Неправильно. Ветки просто удаляются.',
            },
          ],
          explanation:
            'В GitHub Flow ветки удаляются после слияния в main. Это поддерживает чистоту репозитория и упрощает структуру.',
          difficulty: 'easy',
        },
        {
          id: '6-3-q3',
          question: 'Какое обязательное требование для слияния ветки в main в GitHub Flow?',
          options: [
            {
              id: 'a',
              text: 'Пройти code review через Pull Request',
              isCorrect: true,
              explanation: 'Правильно! Pull Request с review обязателен.',
            },
            {
              id: 'b',
              text: 'Иметь не менее 10 коммитов',
              isCorrect: false,
              explanation: 'Неправильно. Количество коммитов не важно.',
            },
            {
              id: 'c',
              text: 'Простоять минимум неделю',
              isCorrect: false,
              explanation: 'Неправильно. Время не ограничено.',
            },
            {
              id: 'd',
              text: 'Получить одобрение менеджера',
              isCorrect: false,
              explanation: 'Неправильно. Code review важнее.',
            },
          ],
          explanation:
            'В GitHub Flow каждое изменение проходит через Pull Request с code review. Это обеспечивает качество кода и обсуждение изменений.',
          difficulty: 'medium',
        },
        {
          id: '6-3-q4',
          question: 'Почему GitHub Flow подходит для continuous deployment?',
          options: [
            {
              id: 'a',
              text: 'Потому что имеет много веток',
              isCorrect: false,
              explanation: 'Неправильно. GitHub Flow имеет минимум веток.',
            },
            {
              id: 'b',
              text: 'Потому что main всегда готов к деплою',
              isCorrect: true,
              explanation: 'Правильно! После слияния в main можно сразу деплоить.',
            },
            {
              id: 'c',
              text: 'Потому что требует долгого тестирования',
              isCorrect: false,
              explanation: 'Неправильно. GitHub Flow предполагает быстрый деплой.',
            },
            {
              id: 'd',
              text: 'Потому что использует release-ветки',
              isCorrect: false,
              explanation: 'Неправильно. GitHub Flow не использует release-ветки.',
            },
          ],
          explanation:
            'В GitHub Flow main всегда готов к деплою. После слияния PR можно немедленно развернуть на production. Это идеально для continuous deployment.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'GitHub Flow Guide',
          url: 'https://docs.github.com/en/get-started/quickstart/github-flow',
          type: 'documentation',
          description: 'Официальное руководство GitHub Flow',
        },
        {
          title: 'Understanding GitHub Flow',
          url: 'https://guides.github.com/introduction/flow/',
          type: 'article',
          description: 'Введение в GitHub Flow от GitHub',
        },
        {
          title: 'GitHub Flow for Teams',
          url: 'https://www.atlassian.com/git/tutorials/comparing-workflows/gitflow-workflow',
          type: 'article',
          description: 'GitHub Flow для команд от Atlassian',
        },
      ],
    },
    {
      id: '6-4',
      moduleId: 'module-6',
      title: 'GitLab Flow',
      slug: 'gitlab-flow',
      order: 4,
      theory: `# GitLab Flow

GitLab Flow — гибридная модель, сочетающая простоту GitHub Flow с гибкостью Git Flow.

## Ключевые особенности

- Environment branches (ветки окружений)
- Issue-driven development
- Merge Request workflow
- Гибкая настройка под проект

## Варианты GitLab Flow

### 1. Environment Branches

\`\`\`
main (production)
  ↑
staging (pre-production)
  ↑
develop (development)
  ↑
feature/issue-123
\`\`\`

### 2. Release Branches

\`\`\`
main ──●────●────●────●
       \\              \\
        release/1.0 ───●
\`\`\`

## Структура веток

### Главные ветки

- **main** — продакшен (production)
- **staging** — предпродакшен (опционально)
- **develop** — разработка (опционально)

### Временные ветки

- **feature/\*** — функции
- **bugfix/\*** — исправление багов
- **hotfix/\*** — срочные исправления

## Рабочий процесс

### 1. Создание Issue

\`\`\`
В GitLab: Issues → New Issue
Заполните: название, описание, labels, assignee
\`\`\`

### 2. Создание ветки из Issue

\`\`\`bash
# GitLab автоматически создаёт ветку
# Или вручную:
git checkout main
git pull origin main
git checkout -b feature/issue-123-add-login
\`\`\`

### 3. Разработка

\`\`\`bash
# Работа над функцией
git add .
git commit -m "Добавить страницу входа (#123)"
git push -u origin feature/issue-123-add-login
\`\`\`

### 4. Merge Request

\`\`\`bash
# Создать MR через GitLab UI
# Target: develop или staging
# Source: feature/issue-123-add-login
\`\`\`

### 5. Продвижение по окружениям

\`\`\`bash
# develop → staging
git checkout staging
git merge --no-ff feature/issue-123-add-login

# staging → main (после тестирования)
git checkout main
git merge --no-ff staging
\`\`\`

## CI/CD Pipeline

\`\`\`yaml
# .gitlab-ci.yml
stages:
  - test
  - staging
  - production

test:
  stage: test
  script: npm test
  only:
    - merge_requests

staging:
  stage: staging
  script: deploy staging
  only:
    - main
  when: manual

production:
  stage: production
  script: deploy production
  only:
    - main
  when: manual
\`\`\`

## Правила слияния

1. **feature → develop**: После code review
2. **develop → staging**: После прохождения тестов
3. **staging → main**: После QA и одобрения

## Защита веток

\`\`\`bash
# В GitLab Settings → Repository → Protected Branches
# main: Maintainers only, require MR
# staging: Developers + Maintainers, require MR
# develop: Developers + Maintainers, require MR
\`\`\`

## Когда использовать GitLab Flow?

**Подходит:**
- Проекты с несколькими окружениями
- Команды с QA-процессом
- Enterprise-разработка
- Проекты на GitLab

**Не подходит:**
- Простые проекты без окружений
- Continuous deployment без staging
`,
      steps: [
        {
          id: 1,
          title: 'Создание ветки для issue',
          description: 'Создайте ветку для issue с номером 42.',
          expectedCommand: 'git checkout -b feature/issue-42 main',
          validationRegex: 'git (checkout|switch)\\s+-b\\s+(feature|bugfix)/issue-.*',
          hint: {
            level1: 'Создайте ветку для issue с номером.',
            level2: 'Используйте git checkout -b feature/issue-номер main.',
            level3: 'Введите: git checkout -b feature/issue-42 main',
          },
        },
      ],
      keyInsight: {
        main: 'GitLab Flow — гибридная модель с environment branches. Подходит для проектов с несколькими окружениями (dev, staging, prod).',
        example:
          'Команда разрабатывает в feature-ветках, сливает в develop для интеграции. После тестирования develop сливается в staging для QA. После одобрения staging идёт в main (production).',
        warning: 'Не пропускайте окружения! Код должен пройти все этапы: develop → staging → main.',
        tip: 'Используйте автоматические пайплайны для продвижения кода между окружениями.',
        additional: [
          'Название ветки должно содержать номер issue: feature/issue-123.',
          'Настройте protected branches для всех главных веток.',
          'Используйте Merge Request templates для единообразия.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '6-4-q1',
          question: 'Что такое environment branches в GitLab Flow?',
          options: [
            {
              id: 'a',
              text: 'Ветки для разных разработчиков',
              isCorrect: false,
              explanation: 'Неправильно. Environment branches связаны с окружениями.',
            },
            {
              id: 'b',
              text: 'Ветки для разных окружений (staging, production)',
              isCorrect: true,
              explanation: 'Правильно! Каждое окружение имеет свою ветку.',
            },
            {
              id: 'c',
              text: 'Ветки для разных функций',
              isCorrect: false,
              explanation: 'Неправильно. Это feature branches.',
            },
            {
              id: 'd',
              text: 'Ветки для разных версий',
              isCorrect: false,
              explanation: 'Неправильно. Это release branches.',
            },
          ],
          explanation:
            'GitLab Flow использует environment branches: main → staging → production. Каждая ветка соответствует окружению деплоя.',
          difficulty: 'easy',
        },
        {
          id: '6-4-q2',
          question: 'Как GitLab Flow связан с issue tracking?',
          options: [
            {
              id: 'a',
              text: 'Никак не связан',
              isCorrect: false,
              explanation: 'Неправильно. GitLab Flow тесно связан с issues.',
            },
            {
              id: 'b',
              text: 'Каждая ветка создаётся из issue и сливается через Merge Request',
              isCorrect: true,
              explanation: 'Правильно! Ветки привязаны к issues.',
            },
            {
              id: 'c',
              text: 'Issues используются только для багов',
              isCorrect: false,
              explanation: 'Неправильно. Issues для всех задач.',
            },
            {
              id: 'd',
              text: 'Merge Request не связан с issues',
              isCorrect: false,
              explanation: 'Неправильно. MR связан с issue.',
            },
          ],
          explanation:
            'GitLab Flow тесно интегрирован с issue tracking: ветки создаются из issues, а Merge Request закрывает issue при слиянии.',
          difficulty: 'medium',
        },
        {
          id: '6-4-q3',
          question: 'В чём преимущество GitLab Flow перед Git Flow?',
          options: [
            {
              id: 'a',
              text: 'Больше веток',
              isCorrect: false,
              explanation: 'Неправильно. GitLab Flow проще.',
            },
            {
              id: 'b',
              text: 'Простота и интеграция с CI/CD',
              isCorrect: true,
              explanation: 'Правильно! GitLab Flow проще и лучше подходит для CI/CD.',
            },
            {
              id: 'c',
              text: 'Нет feature-веток',
              isCorrect: false,
              explanation: 'Неправильно. Feature-ветки есть.',
            },
            {
              id: 'd',
              text: 'Только для больших команд',
              isCorrect: false,
              explanation: 'Неправильно. Подходит для любых команд.',
            },
          ],
          explanation:
            'GitLab Flow — гибридный подход, который проще Git Flow и лучше интегрируется с CI/CD пайплайнами.',
          difficulty: 'medium',
        },
        {
          id: '6-4-q4',
          question: 'Какой порядок слияния веток в GitLab Flow с environment branches?',
          options: [
            {
              id: 'a',
              text: 'production → staging → main',
              isCorrect: false,
              explanation: 'Неправильно. Порядок обратный.',
            },
            {
              id: 'b',
              text: 'main → staging → production',
              isCorrect: true,
              explanation: 'Правильно! Изменения идут от разработки к production.',
            },
            {
              id: 'c',
              text: 'staging → main → production',
              isCorrect: false,
              explanation: 'Неправильно. Неверный порядок.',
            },
            {
              id: 'd',
              text: 'production → main → staging',
              isCorrect: false,
              explanation: 'Неправильно. Неверный порядок.',
            },
          ],
          explanation:
            'В GitLab Flow изменения идут последовательно: feature → main → staging → production. Каждое слияние — деплой на соответствующее окружение.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'GitLab Flow Documentation',
          url: 'https://docs.gitlab.com/ee/topics/gitlab_flow.html',
          type: 'documentation',
          description: 'Официальная документация GitLab Flow',
        },
        {
          title: 'GitLab CI/CD',
          url: 'https://docs.gitlab.com/ee/ci/',
          type: 'documentation',
          description: 'Документация GitLab CI/CD',
        },
        {
          title: 'Merge Request Workflow',
          url: 'https://docs.gitlab.com/ee/user/project/merge_requests/',
          type: 'documentation',
          description: 'Документация GitLab о Merge Requests',
        },
      ],
    },
    {
      id: '6-5',
      moduleId: 'module-6',
      title: 'Trunk-Based Development',
      slug: 'trunk-based-development',
      order: 5,
      theory: `# Trunk-Based Development

Trunk-Based Development (TBD) — методология с минимальным ветвлением и частыми слияниями.

## Принципы

1. **Одна главная ветка (trunk/main)** — весь код здесь
2. **Короткоживущие ветки** — не более 1-2 дней
3. **Частые слияния** — несколько раз в день
4. **Feature Flags** — незавершённый код скрыт флагами
5. **Быстрые тесты** — CI за 10-15 минут

## Структура веток

\`\`\`
main    ●─●─●─●─●─●─●─●─●─●─●─●─●
        /   /   /   /   /   /
short   ●─●   ●─●   ●   ●─●
lived
branches
\`\`\`

Ветки живут несколько часов, максимум пару дней.

## Рабочий процесс

### 1. Получение последних изменений

\`\`\`bash
git checkout main
git pull origin main
\`\`\`

### 2. Создание короткой ветки

\`\`\`bash
git checkout -b add-user-profile
\`\`\`

### 3. Быстрая разработка

\`\`\`bash
# Делайте маленькие коммиты
git add .
git commit -m "Добавить компонент профиля"
git push -u origin add-user-profile

# Слейте как можно быстрее (в тот же день)
\`\`\`

### 4. Слияние через PR

\`\`\`bash
# Создайте PR
# Пройдите code review
# Слейте в main
\`\`\`

Или напрямую (для небольших команд):

\`\`\`bash
git checkout main
git pull origin main
git merge add-user-profile
git push origin main
\`\`\`

## Feature Flags

Незавершённый код скрыт флагами:

\`\`\`javascript
// Пример feature flag
if (featureFlags.isEnabled('new-profile-page')) {
  renderNewProfilePage();
} else {
  renderOldProfilePage();
}
\`\`\`

### Управление флагами

\`\`\`javascript
// Конфигурация флагов
const featureFlags = {
  'new-profile-page': {
    enabled: true,
    rollout: 50, // 50% пользователей
    environments: ['staging', 'production']
  },
  'dark-mode': {
    enabled: false,
    environments: ['development']
  }
};
\`\`\`

## Инструменты для TBD

### Feature Flags
- LaunchDarkly
- Split
- Flagsmith
- Unleash

### CI/CD
- GitHub Actions
- GitLab CI
- Jenkins
- CircleCI

## Правила TBD

### Обязательные

1. **CI < 15 минут** — иначе разработчики не будут ждать
2. **Feature flags** — для незавершённого кода
3. **Code review < 1 часа** — быстрые ревью
4. **Маленькие изменения** — до 400 строк

### Рекомендуемые

1. **Pair programming** — ревью в процессе
2. **Автотесты** — минимум 80% покрытия
3. **Мониторинг** — быстрое обнаружение проблем
4. **Rollback** — возможность быстрого отката

## Сравнение с другими методологиями

| Аспект | TBD | GitHub Flow | Git Flow |
|--------|-----|-------------|----------|
| Веток | 1 | 1 | 5+ |
| Время в ветке | Часы | Дни-недели | Недели |
| Feature Flags | Да | Нет | Нет |
| Сложность | Низкая | Низкая | Высокая |
| Требования к тестам | Высокие | Средние | Средние |

## Когда использовать TBD?

**Подходит:**
- Опытные команды
- Хорошее тестовое покрытие
- Continuous deployment
- Быстрые итерации

**Не подходит:**
- Новички в команде
- Слабое тестирование
- Длинные циклы разработки
- Требование стабильности любой ценой
`,
      steps: [
        {
          id: 1,
          title: 'Создание короткой ветки',
          description: 'Создайте короткоживущую ветку для небольшого изменения.',
          expectedCommand: 'git checkout -b small-fix main',
          validationRegex: 'git (checkout|switch)\\s+-b\\s+\\S+\\s+main',
          hint: {
            level1: 'Создайте короткую ветку от main.',
            level2: 'Используйте git checkout -b имя-ветки main.',
            level3: 'Введите: git checkout -b small-fix main',
          },
        },
        {
          id: 2,
          title: 'Быстрое слияние',
          description: 'Слейте ветку обратно в main (имитация быстрого цикла).',
          expectedCommand: 'git checkout main && git merge small-fix',
          validationRegex: 'git\\s+checkout\\s+main.*git\\s+merge\\s+small-fix',
          hint: {
            level1: 'Переключитесь на main и слейте ветку.',
            level2: 'Используйте git checkout main && git merge small-fix.',
            level3: 'Введите: git checkout main && git merge small-fix',
          },
        },
      ],
      keyInsight: {
        main: 'Trunk-Based Development — методология для опытных команд с минимальным ветвлением и частыми слияниями в main.',
        example:
          'Разработчик создаёт ветку в 9 утра, делает 3 коммита, открывает PR, получает ревью за 30 минут, сливает в main к обеду. Незавершённый код скрыт feature flag.',
        warning:
          'TBD требует высокого качества тестов! Без надёжных тестов вы будете часто ломать main.',
        tip: 'Начните с GitHub Flow и переходите к TBD когда команда наберётся опыта и тесты будут надёжными.',
        additional: [
          'Feature flags — обязательная часть TBD.',
          'CI должен быть быстрым (< 15 минут).',
          'Маленькие PR сливаются быстрее и вызывают меньше конфликтов.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '6-5-q1',
          question: 'Что такое trunk в Trunk-Based Development?',
          options: [
            {
              id: 'a',
              text: 'Отдельная ветка для релизов',
              isCorrect: false,
              explanation: 'Неправильно. Trunk — это основная ветка разработки.',
            },
            {
              id: 'b',
              text: 'Основная ветка (main/master) для всей разработки',
              isCorrect: true,
              explanation: 'Правильно! Trunk — это main, где происходит вся разработка.',
            },
            {
              id: 'c',
              text: 'Ветка для hotfix',
              isCorrect: false,
              explanation: 'Неправильно. Hotfix делаются напрямую в trunk.',
            },
            {
              id: 'd',
              text: 'Специальная ветка для тестирования',
              isCorrect: false,
              explanation: 'Неправильно. Тестирование происходит до слияния.',
            },
          ],
          explanation:
            'Trunk (main/master) — единственная долгоживущая ветка в Trunk-Based Development. Все разработчики коммитят напрямую или через очень короткоживущие ветки.',
          difficulty: 'easy',
        },
        {
          id: '6-5-q2',
          question: 'Какой максимальный срок жизни feature-ветки в Trunk-Based Development?',
          options: [
            {
              id: 'a',
              text: 'Один месяц',
              isCorrect: false,
              explanation: 'Неправильно. Это слишком долго для Trunk-Based.',
            },
            {
              id: 'b',
              text: 'Один-два дня',
              isCorrect: true,
              explanation: 'Правильно! Ветки должны быть очень короткоживущими.',
            },
            {
              id: 'c',
              text: 'Одна неделя',
              isCorrect: false,
              explanation: 'Неправильно. Это слишком долго.',
            },
            {
              id: 'd',
              text: 'Без ограничений',
              isCorrect: false,
              explanation: 'Неправильно. Ветки должны быть короткоживущими.',
            },
          ],
          explanation:
            'В Trunk-Based Development feature-ветки живут 1-2 дня максимум. Часто разработчики коммитят напрямую в trunk несколько раз в день.',
          difficulty: 'medium',
        },
        {
          id: '6-5-q3',
          question: 'Какое обязательное требование для успешного Trunk-Based Development?',
          options: [
            {
              id: 'a',
              text: 'Большая команда',
              isCorrect: false,
              explanation: 'Неправильно. Размер команды не важен.',
            },
            {
              id: 'b',
              text: 'Надёжные автоматические тесты и CI/CD',
              isCorrect: true,
              explanation: 'Правильно! Без автоматизации Trunk-Based невозможен.',
            },
            {
              id: 'c',
              text: 'Длинные релизные циклы',
              isCorrect: false,
              explanation: 'Неправильно. Trunk-Based для частых релизов.',
            },
            {
              id: 'd',
              text: 'Много веток',
              isCorrect: false,
              explanation: 'Неправильно. Trunk-Based минимизирует ветки.',
            },
          ],
          explanation:
            'Trunk-Based Development требует надёжных автоматических тестов и CI/CD, чтобы убедиться, что trunk всегда готов к деплою.',
          difficulty: 'medium',
        },
        {
          id: '6-5-q4',
          question: 'Что такое feature flags в контексте Trunk-Based Development?',
          options: [
            {
              id: 'a',
              text: 'Флаги для создания веток',
              isCorrect: false,
              explanation: 'Неправильно. Feature flags не связаны с ветками.',
            },
            {
              id: 'b',
              text: 'Переключатели для включения/выключения незавершённых функций',
              isCorrect: true,
              explanation: 'Правильно! Позволяют мержить незавершённый код.',
            },
            {
              id: 'c',
              text: 'Флаги для блокировки коммитов',
              isCorrect: false,
              explanation: 'Неправильно. Feature flags не блокируют коммиты.',
            },
            {
              id: 'd',
              text: 'Флаги для управления ветками',
              isCorrect: false,
              explanation: 'Неправильно. Feature flags управляют функциями, не ветками.',
            },
          ],
          explanation:
            'Feature flags позволяют мержить незавершённый код в trunk, но скрывать его от пользователей. Это ключевой паттерн Trunk-Based Development.',
          difficulty: 'hard',
        },
      ],
      resources: [
        {
          title: 'Trunk Based Development',
          url: 'https://trunkbaseddevelopment.com/',
          type: 'article',
          description: 'Полное руководство по Trunk-Based Development',
        },
        {
          title: 'Feature Flags Guide',
          url: 'https://www.featureflags.io/',
          type: 'tool',
          description: 'Введение в feature flags',
        },
        {
          title: 'Continuous Integration Best Practices',
          url: 'https://martinfowler.com/articles/continuousIntegration.html',
          type: 'article',
          description: 'Лучшие практики CI от Martin Fowler',
        },
      ],
    },
    {
      id: '6-6',
      moduleId: 'module-6',
      title: 'Выбор и адаптация методологии',
      slug: 'choosing-methodology',
      order: 6,
      theory: `# Выбор и адаптация методологии

Нет идеальной методологии — есть подходящая для вашей ситуации.

## Критерии выбора

### Размер команды

| Команда | Рекомендация |
|---------|--------------|
| 1-3 человека | GitHub Flow |
| 4-10 человек | GitHub Flow / GitLab Flow |
| 10-30 человек | GitLab Flow / Git Flow |
| 30+ человек | Git Flow / Своя методология |

### Частота релизов

| Релизы | Методология |
|--------|-------------|
| Несколько в день | TBD / GitHub Flow |
| Ежедневно | GitHub Flow |
| Еженедельно | GitLab Flow |
| Ежемесячно | Git Flow |
| По расписанию | Git Flow |

### Тип продукта

| Продукт | Методология |
|---------|-------------|
| SaaS / Web | GitHub Flow / TBD |
| Мобильное приложение | GitLab Flow |
| Коробочный софт | Git Flow |
| Библиотека | GitHub Flow / GitLab Flow |

### Опыт команды

| Опыт | Рекомендация |
|------|--------------|
| Новички | GitHub Flow |
| Средний | GitLab Flow |
| Продвинутый | TBD / Git Flow |

## Гибридные подходы

### GitHub Flow + Release Branches

\`\`\`bash
# Основная работа в GitHub Flow
main ──●────●────●────●
       /     /     /
feature ●────●     ●

# Но для релизов создаются ветки
release/1.0 ────────●────●
\`\`\`

### GitLab Flow без staging

\`\`\`bash
# Упрощённый GitLab Flow
main (production)
  ↑
develop
  ↑
feature/issue-123
\`\`\`

### Git Flow с feature flags

\`\`\`bash
# Git Flow структура веток
# Но с feature flags для раннего слияния в develop
\`\`\`

## Внедрение методологии

### Шаг 1: Документация

\`\`\`markdown
# Наша методология ветвления

## Названия веток
- feature/название — новые функции
- bugfix/название — исправление багов
- hotfix/название — срочные исправления

## Процесс
1. Создать ветку от main
2. Разработать и протестировать
3. Открыть Pull Request
4. Получить одобрение ревьюера
5. Слить в main

## Правила
- PR не более 400 строк
- Тесты обязательны
- Описание PR обязательно
\`\`\`

### Шаг 2: Автоматизация

\`\`\`yaml
# .github/workflows/pr-check.yml
name: PR Check
on: pull_request
jobs:
  check:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      - name: Check branch name
        run: |
          if [[ ! "$GITHUB_HEAD_REF" =~ ^(feature|bugfix|hotfix)/ ]]; then
            echo "Invalid branch name"
            exit 1
          fi
      - name: Run tests
        run: npm test
\`\`\`

### Шаг 3: Защита веток

\`\`\`bash
# GitHub: Settings → Branches → Add rule
# - Require pull request reviews
# - Require status checks
# - Require linear history
\`\`\`

### Шаг 4: Обучение команды

1. Проведите воркшоп по методологии
2. Создайте шпаргалку с командами
3. Настройте PR template
4. Регулярно обсуждайте на ретроспективах

## Признаки проблем

### Методология не работает, если:

- Частые конфликты при слиянии
- Долгие PR (больше недели)
- Непонятно, куда сливать код
- Main часто сломан
- Команда игнорирует правила

### Решения:

1. **Упростите методологию** — меньше правил
2. **Улучшите коммуникацию** — обсуждайте проблемы
3. **Автоматизируйте проверки** — CI/CD
4. **Обучите команду** — воркшопы, парное программирование

## Эволюция методологии

\`\`\`
GitHub Flow → GitLab Flow → Git Flow
    ↑              ↑           ↑
    └──────────────┴───────────┘
           По мере роста команды и сложности проекта
\`\`\`

Начните просто, усложняйте при необходимости.
`,
      steps: [
        {
          id: 1,
          title: 'Анализ текущих веток',
          description: 'Посмотрите текущую структуру веток в репозитории.',
          expectedCommand: 'git branch -a',
          hint: {
            level1: 'Посмотрите все ветки.',
            level2: 'Используйте git branch -a.',
            level3: 'Введите: git branch -a',
          },
        },
        {
          id: 2,
          title: 'Просмотр истории',
          description: 'Посмотрите историю коммитов для понимания стиля работы.',
          expectedCommand: 'git log --oneline --graph --all',
          hint: {
            level1: 'Посмотрите историю с графом.',
            level2: 'Используйте git log --oneline --graph --all.',
            level3: 'Введите: git log --oneline --graph --all',
          },
        },
      ],
      keyInsight: {
        main: 'Выбирайте методологию под команду и проект. Начинайте просто, усложняйте при необходимости.',
        example:
          'Стартап из 3 разработчиков начинает с GitHub Flow. Через год команда выросла до 15 человек, добавили staging — перешли на GitLab Flow. Ещё через год — Git Flow для управляемых релизов.',
        warning: 'Не копируйте методологию слепо! Адаптируйте под свою команду и процессы.',
        tip: 'Пересматривайте методологию на ретроспективах. То, что работало полгода назад, может не работать сейчас.',
        additional: [
          'Документируйте методологию и делайте её доступной для всех.',
          'Автоматизируйте проверки для соблюдения правил.',
          'Обучайте новых членов команды методологии.',
        ],
      },
      estimatedTime: 20,
      quizQuestions: [
        {
          id: '6-6-q1',
          question:
            'Какая методология лучше всего подходит для проекта с регулярными релизами каждые 2 недели?',
          options: [
            {
              id: 'a',
              text: 'GitHub Flow',
              isCorrect: false,
              explanation: 'Неправильно. GitHub Flow для continuous deployment.',
            },
            {
              id: 'b',
              text: 'Git Flow',
              isCorrect: true,
              explanation: 'Правильно! Git Flow идеален для проектов с релизными циклами.',
            },
            {
              id: 'c',
              text: 'Trunk-Based Development',
              isCorrect: false,
              explanation: 'Неправильно. Trunk-Based для частых деплоев.',
            },
            {
              id: 'd',
              text: 'Никакая не подходит',
              isCorrect: false,
              explanation: 'Неправильно. Git Flow подходит для таких проектов.',
            },
          ],
          explanation:
            'Git Flow разработан для проектов с регулярными релизными циклами. Release-ветки позволяют подготовить и протестировать релиз.',
          difficulty: 'medium',
        },
        {
          id: '6-6-q2',
          question: 'Какой фактор наиболее важен при выборе методологии ветвления?',
          options: [
            {
              id: 'a',
              text: 'Размер команды',
              isCorrect: false,
              explanation: 'Неправильно. Это важно, но не единственный фактор.',
            },
            {
              id: 'b',
              text: 'Частота релизов и наличие CI/CD',
              isCorrect: true,
              explanation: 'Правильно! Это ключевые факторы выбора.',
            },
            {
              id: 'c',
              text: 'Предпочтения менеджера',
              isCorrect: false,
              explanation: 'Неправильно. Технические факторы важнее.',
            },
            {
              id: 'd',
              text: 'Возраст проекта',
              isCorrect: false,
              explanation: 'Неправильно. Возраст не определяет методологию.',
            },
          ],
          explanation:
            'При выборе методологии важно учитывать: частоту релизов, наличие CI/CD, опыт команды, требования к стабильности.',
          difficulty: 'medium',
        },
        {
          id: '6-6-q3',
          question: 'Можно ли комбинировать элементы разных методологий?',
          options: [
            {
              id: 'a',
              text: 'Нет, нужно строго следовать одной методологии',
              isCorrect: false,
              explanation: 'Неправильно. Адаптация приветствуется.',
            },
            {
              id: 'b',
              text: 'Да, можно адаптировать под нужды команды',
              isCorrect: true,
              explanation: 'Правильно! Методологии — это руководства, не догмы.',
            },
            {
              id: 'c',
              text: 'Только для больших команд',
              isCorrect: false,
              explanation: 'Неправильно. Любая команда может адаптировать.',
            },
            {
              id: 'd',
              text: 'Только с разрешения Git',
              isCorrect: false,
              explanation: 'Неправильно. Git не регулирует методологии.',
            },
          ],
          explanation:
            'Методологии ветвления — это руководства. Команды могут адаптировать и комбинировать элементы для своих нужд.',
          difficulty: 'easy',
        },
        {
          id: '6-6-q4',
          question: 'Какая методология требует наименьшего количества долгоживущих веток?',
          options: [
            {
              id: 'a',
              text: 'Git Flow',
              isCorrect: false,
              explanation: 'Неправильно. Git Flow имеет много долгоживущих веток.',
            },
            {
              id: 'b',
              text: 'GitHub Flow и Trunk-Based Development',
              isCorrect: true,
              explanation: 'Правильно! Оба подхода используют одну основную ветку.',
            },
            {
              id: 'c',
              text: 'GitLab Flow',
              isCorrect: false,
              explanation: 'Неправильно. GitLab Flow использует environment branches.',
            },
            {
              id: 'd',
              text: 'Все методологии одинаковы',
              isCorrect: false,
              explanation: 'Неправильно. Методологии различаются количеством веток.',
            },
          ],
          explanation:
            'GitHub Flow и Trunk-Based Development используют только одну долгоживущую ветку (main/trunk), что упрощает структуру.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Flow Original Article',
          url: 'https://nvie.com/posts/a-successful-git-branching-model/',
          type: 'article',
        },
        {
          title: 'GitHub Flow Guide',
          url: 'https://docs.github.com/en/get-started/quickstart/github-flow',
          type: 'documentation',
        },
        {
          title: 'GitLab Flow Documentation',
          url: 'https://docs.gitlab.com/ee/topics/gitlab_flow.html',
          type: 'documentation',
        },
        {
          title: 'Trunk Based Development',
          url: 'https://trunkbaseddevelopment.com/',
          type: 'article',
        },
      ],
    },
  ],
};

export { module6 };
